import { useState } from 'react';

import { useSearchPageState } from '@klareospace/pharos-lib-front-utils';
import type { Site, SiteType, SortDirection } from '@klareospace/pharos-lib-types';

import { useSites } from './use-sites';
import type { SiteSortableColumn } from '../types/site.types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseSitesPageOptions {
  spaceId: string;
}

export interface UseSitesPageReturn {
  currentPage: number;
  error: Error | null;
  isLoading: boolean;
  onPageChange: (newPage: number) => void;
  onSearchChange: (value: string) => void;
  onSort: (sortBy: SiteSortableColumn, sortDirection: SortDirection | null) => void;
  onTypeFilterChange: (values: string[]) => void;
  pageSize: number;
  searchValue: string;
  sites: Site[];
  sortBy: SiteSortableColumn | undefined;
  sortDirection: SortDirection | undefined;
  totalItems: number;
  totalPages: number;
  typeFilter: SiteType[];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSitesPage({ spaceId }: UseSitesPageOptions): UseSitesPageReturn {
  const searchState = useSearchPageState<SiteSortableColumn>();
  const [typeFilter, setTypeFilter] = useState<SiteType[]>([]);

  const { data, error, isLoading } = useSites({
    name: searchState.debouncedSearch || undefined,
    page: searchState.currentPage,
    pageSize: searchState.pageSize,
    sort: searchState.sort,
    spaceIds: [spaceId],
    types: typeFilter.length > 0 ? typeFilter : undefined,
  });

  const onTypeFilterChange = (values: string[]): void => {
    // Values come from a controlled multi-select whose options are SiteType constants — cast is safe.
    setTypeFilter(values as SiteType[]);
    searchState.onPageChange(1);
  };

  return {
    currentPage: searchState.currentPage,
    error: error ?? null,
    isLoading,
    onPageChange: searchState.onPageChange,
    onSearchChange: searchState.onSearchChange,
    onSort: searchState.onSort,
    onTypeFilterChange,
    pageSize: searchState.pageSize,
    searchValue: searchState.searchValue,
    sites: data?.items ?? [],
    sortBy: searchState.sortBy,
    sortDirection: searchState.sortDirection,
    totalItems: data?.total ?? 0,
    totalPages: data?.totalPages ?? 1,
    typeFilter,
  };
}
